import React, { useCallback } from 'react';
import {
    DmsTextField,
    DmsSwitch,
    useGet,
    fetchData,
    HTTP_STATUS,
    intl
} from 'dmsbasereact';
import { Box, Typography, Grid } from '@mui/material';
import { SettingsViewEdit, SettingsField, SettingsRow } from '../../components/SettingsLayouts';

/**
 * SSO View Content - displays SSO settings as read-only
 */
const SsoViewContent = ({ data }) => {
    return (
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                    {intl.get('Enabled').d('Enabled')}
                </Typography>
                <Typography variant="body1">
                    {data.enabled ? intl.get('Enabled').d('Enabled') : intl.get('Disabled').d('Disabled')}
                </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                    {intl.get('Provider').d('Provider')}
                </Typography>
                <Typography variant="body1">{data.provider || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                    {intl.get('ClientId').d('Client ID')}
                </Typography>
                <Typography variant="body1">{data.client_id || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                    {intl.get('TenantId').d('Tenant ID')}
                </Typography>
                <Typography variant="body1">{data.tenant_id || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                    {intl.get('ClientSecret').d('Client Secret')}
                </Typography>
                <Typography variant="body1">********</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                    {intl.get('AuthorityUrl').d('Authority URL')}
                </Typography>
                <Typography variant="body1">{data.authority_url || '-'}</Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                    {intl.get('RedirectUri').d('Redirect URI')}
                </Typography>
                <Typography variant="body1">{data.redirect_uri || '-'}</Typography>
            </Grid>
        </Grid>
    );
};

/**
 * SSO Edit Content - displays SSO settings as editable form fields
 */
const SsoEditContent = ({ formData, onFieldChange }) => {
    return (
        <Grid container spacing={2}>
            <SettingsField sm={12}>
                <SettingsRow>
                    <DmsSwitch
                        name="enabled"
                        label={intl.get('EnableSSO').d('Enable SSO')}
                        checked={formData.enabled ?? false}
                        onChange={(name, value) => {
                            const boolValue = value === 'true' || value === true;
                            onFieldChange(name, boolValue);
                        }}
                    />
                </SettingsRow>
            </SettingsField>
            <SettingsField sm={6} md={3}>
                <DmsTextField
                    name="provider"
                    label={intl.get('Provider').d('Provider')}
                    fullWidth
                    value={formData.provider ?? ''}
                    disabled
                />
            </SettingsField>
            <SettingsField sm={6} md={3}>
                <DmsTextField
                    name="client_id"
                    label={intl.get('ClientId').d('Client ID')}
                    fullWidth
                    value={formData.client_id ?? ''}
                    onChange={(e) => onFieldChange('client_id', e.target.value)}
                />
            </SettingsField>
            <SettingsField sm={6} md={3}>
                <DmsTextField
                    name="tenant_id"
                    label={intl.get('TenantId').d('Tenant ID')}
                    fullWidth
                    value={formData.tenant_id ?? ''}
                    onChange={(e) => onFieldChange('tenant_id', e.target.value)}
                />
            </SettingsField>
            <SettingsField sm={6} md={3}>
                <DmsTextField
                    name="client_secret"
                    label={intl.get('ClientSecret').d('Client Secret')}
                    type="password"
                    fullWidth
                    helperText={intl.get('LeaveBlankToKeep').d('Leave blank to keep existing')}
                    onChange={(e) => onFieldChange('client_secret', e.target.value)}
                />
            </SettingsField>
            <SettingsField sm={6} md={6}>
                <DmsTextField
                    name="authority_url"
                    label={intl.get('AuthorityUrl').d('Authority URL')}
                    fullWidth
                    value={formData.authority_url ?? ''}
                    onChange={(e) => onFieldChange('authority_url', e.target.value)}
                />
            </SettingsField>
            <SettingsField sm={6} md={6}>
                <DmsTextField
                    name="redirect_uri"
                    label={intl.get('RedirectUri').d('Redirect URI')}
                    fullWidth
                    value={formData.redirect_uri ?? ''}
                    onChange={(e) => onFieldChange('redirect_uri', e.target.value)}
                />
            </SettingsField>
        </Grid>
    );
};

/**
 * AuthenticationSettings - SSO configuration.
 * Uses SettingsViewEdit pattern with view/edit mode.
 */
export const AuthenticationSettings = () => {
    // SSO data fetching
    const { data: ssoData, isLoading: ssoLoading, refetch: refetchSso } = useGet('/sso_settings', null, true);

    // SSO handlers
    const prepareSsoFormData = useCallback((data) => {
        return {
            provider: data.provider || 'microsoft',
            client_id: data.client_id || '',
            tenant_id: data.tenant_id || '',
            authority_url: data.authority_url || 'https://login.microsoftonline.com',
            redirect_uri: data.redirect_uri || '',
            enabled: data.enabled ?? false,
        };
    }, []);

    const buildSsoPayload = useCallback((formData) => {
        const payload = {
            provider: formData.provider,
            client_id: formData.client_id,
            tenant_id: formData.tenant_id,
            authority_url: formData.authority_url,
            redirect_uri: formData.redirect_uri,
            enabled: formData.enabled,
        };
        // Only include client_secret if it was changed (not empty)
        if (formData.client_secret) {
            payload.client_secret = formData.client_secret;
        }
        return payload;
    }, []);

    const handleSsoSave = useCallback(async (payload) => {
        try {
            const response = await fetchData('/sso_settings', 'POST', true, payload, false, true);
            if (response.status === HTTP_STATUS.OK_200 || response.status === HTTP_STATUS.CREATED_201) {
                return response.data?.data || response.data;
            }
            return null;
        } catch (error) {
            console.error('Failed to save SSO settings:', error);
            return null;
        }
    }, []);

    // Prepare data for components
    const ssoSettings = ssoData?.data ?? {
        provider: 'microsoft',
        client_id: '',
        tenant_id: '',
        authority_url: 'https://login.microsoftonline.com',
        redirect_uri: '',
        enabled: false
    };

    return (
        <Box>
            {/* SSO Section */}
            <SettingsViewEdit
                data={ssoSettings}
                loading={ssoLoading}
                title={intl.get('SSOAuthentication').d('Single Sign-On (SSO)')}
                description={intl.get('SSODescription').d(
                    'Configure Microsoft Entra ID (Azure AD) for single sign-on authentication.'
                )}
                viewContent={SsoViewContent}
                editContent={SsoEditContent}
                prepareFormData={prepareSsoFormData}
                buildPayload={buildSsoPayload}
                onSave={handleSsoSave}
                onDataUpdated={refetchSso}
                confirmMessage={intl.get('ConfirmSaveSSOMessage').d(
                    'Are you sure you want to save the SSO settings?'
                )}
            />
        </Box>
    );
};
